import { useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { CRMPipelineKanban } from '../../components/admin/crm/CRMPipelineKanban';
import { CreateProspectModal } from '../../components/admin/crm/CreateProspectModal';
import { CRMProspect } from '../../utils/crmScoringService';

export function CRMPipelinePage() {
  const [selectedProspect, setSelectedProspect] = useState<CRMProspect | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <AdminLayout
      title="Pipeline CRM"
      description="Vue Kanban du pipeline commercial"
      action={{
        label: 'Nouveau Prospect',
        icon: Plus,
        onClick: () => setShowCreateModal(true),
      }}
    >
      <div className="space-y-6">
        <CRMPipelineKanban key={refreshKey} onProspectClick={setSelectedProspect} />

        {selectedProspect && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedProspect.organization_name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">{selectedProspect.sector}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Contact</h3>
                  <div className="space-y-1 text-sm">
                    {selectedProspect.contact_name && (
                      <p><span className="font-medium">Nom:</span> {selectedProspect.contact_name}</p>
                    )}
                    {selectedProspect.contact_email && (
                      <p><span className="font-medium">Email:</span> {selectedProspect.contact_email}</p>
                    )}
                    {selectedProspect.contact_phone && (
                      <p><span className="font-medium">Téléphone:</span> {selectedProspect.contact_phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Données Financières</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Trésorerie estimée</p>
                      <p className="font-semibold">{selectedProspect.estimated_cash.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Dépenses mensuelles</p>
                      <p className="font-semibold">{selectedProspect.estimated_monthly_expenses.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Survie (mois)</p>
                      <p className="font-semibold">{selectedProspect.survival_months}m</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Score SPT</p>
                      <p className="font-semibold">{selectedProspect.spt_score}/4</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Commercial</h3>
                  <div className="space-y-2 text-sm">
                    {selectedProspect.targeted_offer && (
                      <div>
                        <p className="text-gray-500">Offre ciblée</p>
                        <p className="font-semibold">{selectedProspect.targeted_offer}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500">MRR estimé</p>
                      <p className="font-semibold text-green-700">{selectedProspect.estimated_mrr.toLocaleString('fr-FR')} €/mois</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Probabilité de conversion</p>
                      <p className="font-semibold">{Math.round((selectedProspect.dynamic_probability || 0) * 100)}%</p>
                    </div>
                  </div>
                </div>

                {selectedProspect.declic_reached && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-1">Déclic Atteint</h3>
                    {selectedProspect.declic_phrase && (
                      <p className="text-sm text-blue-700 italic">{selectedProspect.declic_phrase}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setSelectedProspect(null)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Fermer
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Modifier
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreateModal && (
          <CreateProspectModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setRefreshKey(prev => prev + 1);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
